import {
  KpiConfigPeriod,
  KpiConfigPeriodToName,
  KpiSection,
} from '../../../data-models/company-financials.data-model';
import { IField, KpiConfigScenario, KpiConfigSection } from '../../../data-models/field2.data-model';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';

/**
 * The kinds of field this admin section unifies. `column` is labelled "System"
 * throughout the UI; the union member keeps the storage name it addresses —
 * the `/column` endpoint, and the `column:<id>` row ids.
 */
export type FieldKind = 'kpi' | 'custom' | 'column' | 'formula';

/** Grid pill selection: the union view or a single kind. */
export type FieldPill = 'all' | FieldKind;

export const FIELD_KINDS: FieldKind[] = ['kpi', 'custom', 'column', 'formula'];

export const FIELD_KIND_LABEL: Record<FieldKind, string> = {
  kpi: 'KPI',
  custom: 'Custom',
  column: 'System',
  formula: 'Formula',
};

/** Items of the header's Create menu — each opens that kind's create form. */
export const FIELD_KIND_CREATE_LABEL: Record<FieldKind, string> = {
  kpi: 'Create KPI',
  custom: 'Create Custom Field',
  column: 'Create System Field',
  formula: 'Create Formula Field',
};

export const PILL_LABEL: Record<FieldPill, string> = {
  all: 'All',
  kpi: 'KPIs',
  custom: 'Custom',
  column: 'System',
  formula: 'Formulas',
};

export const FIELD_PILLS: FieldPill[] = ['all', 'kpi', 'custom', 'column', 'formula'];

/** Label for one synthesized KPI column, e.g. "Month (Actual)", "Quarter (latest value) (Budget)". */
export function kpiDisplayComboLabel(period: KpiConfigPeriod, section: KpiSection): string {
  const periodLabel =
    period === KpiConfigPeriod.Latest ? 'Quarter (latest value)' : KpiConfigPeriodToName[period];
  return `${periodLabel} (${section === KpiSection.actual ? 'Actual' : 'Budget'})`;
}

// Mirrors the BE column fan-out: the periods each scenario can synthesize.
export const END_OF_PERIOD_DISPLAY_PERIODS: KpiConfigPeriod[] = [
  KpiConfigPeriod.Latest,
  KpiConfigPeriod.GrowthMonth,
  KpiConfigPeriod.GrowthQuarter,
  KpiConfigPeriod.GrowthYear,
];
export const ACCRUED_DISPLAY_PERIODS: KpiConfigPeriod[] = [
  KpiConfigPeriod.Month,
  KpiConfigPeriod.Quarter,
  KpiConfigPeriod.Year,
  KpiConfigPeriod.GrowthMonth,
  KpiConfigPeriod.GrowthQuarter,
  KpiConfigPeriod.GrowthYear,
  KpiConfigPeriod.LTM,
  KpiConfigPeriod.CYTD,
];

/** Sections a KPI's Scenario field (`sectionType`) fans out to. */
export function kpiDisplaySections(sectionType: KpiConfigSection | undefined): KpiSection[] {
  if (sectionType === KpiConfigSection.Actual) {
    return [KpiSection.actual];
  }
  if (sectionType === KpiConfigSection.Budget) {
    return [KpiSection.budget];
  }
  return [KpiSection.actual, KpiSection.budget];
}

export const kpiDisplayComboKey = (period: KpiConfigPeriod, section: KpiSection) => `${period}|${section}`;

/** Every (period, section) combination a KPI's config can synthesize, as combo keys. */
export function kpiSupportedComboKeys(kpi: IField<unknown>): Set<string> {
  const periodList =
    kpi.kpiScenario === KpiConfigScenario.EndOfPeriod
      ? END_OF_PERIOD_DISPLAY_PERIODS
      : ACCRUED_DISPLAY_PERIODS;
  const formPeriods = new Set(kpi.periods ?? []);
  const sections = kpiDisplaySections(kpi.sectionType);
  const keys = new Set<string>();
  for (const period of periodList) {
    if (!formPeriods.has(period)) {
      continue;
    }
    for (const section of sections) {
      keys.add(kpiDisplayComboKey(period, section));
    }
  }
  return keys;
}

/**
 * A row in the "All" view: the shared `FieldDefinition` display fields plus the
 * kind discriminator and the original native record (`source`) so the edit
 * action can open that type's own form.
 */
export interface UnifiedFieldRow extends FieldDefinition {
  rowKey: string;
  kind: FieldKind;
  id: number;
  source: unknown;
}
